<script setup lang="ts">
import { ContentComp, DisplayMisses, DropdownSelect, ListComp, ToolbarComp } from '@/components';
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue';
import type { GridColumnOptions } from '@/components/Grid/Classes';
import type { IGridRowCssRule } from '@/components/Grid/Classes/IGridRowCssRule';
import { useDialog, useKeyboardListener, useTaskLoader, useTransactions } from '@/composeables';
import { DropdownItemButton, type SelectLine } from '@/objects';
import { RaceContestant, ShootingResult, type RaceContestantDetails } from '@/objects/generated';
import type ListCompExpose from '@/objects/ListCompExpose';
import { useEventCommonStoreRef, useGuiStore, useShootingResultStore, useToastStore } from '@/stores';
import { computed, ref, watch } from 'vue';
import { propertyof } from '@/utilities';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import ContestantShootingDisplayPoint from './ContestantShootingDisplayPoint.vue';
import { ThreadSleep } from '@/utilities/utils';
import ContestantShootingToggleStateButton from './ContestantShootingToggleStateButton.vue';
import MatrixStationRoleCellShootingEditor from '../Matrix/SubComponents/MatrixStationRoleCellEditors/MatrixStationRoleCellShootingEditor.vue';

const listComp = ref<ListCompExpose<ShootingResult> | null>(null);

const contestant = defineModel<RaceContestantDetails>({ required: true });

const { AddLoader, LoadState } = useTaskLoader();

const { Races } = useEventCommonStoreRef();

const data = ref<ShootingResult[]>([]);

// Set the initial value for the contestant
const firstRaceContestant: RaceContestant | null = contestant.value.RaceContestants.length > 0 ? contestant.value.RaceContestants[0] : null;

// The currently selected race contestant
const selectedRaceContestantId = ref<number | null>(firstRaceContestant?.ID ?? null);

// We create the datasource for the dropdown with the race contestants
const computedRacecontestantsAsSelectline = computed<SelectLine[]>(() => {
    return contestant.value.RaceContestants.map<SelectLine>((rc) => {
        return {
            value: rc.ID,
            text: Races.value.find((x) => x.ID == rc.RaceID)?.Name ?? '',
        };
    });
});

/**
 * Load the data for the view. We load all the data from the backend
 */
const LoadData = () => {
    AddLoader(async () => {
        const shootingResultsStore = useShootingResultStore();
        await shootingResultsStore.EnsureLoaded();
        data.value = shootingResultsStore.GetShootingResultsForContestantId(contestant.value.ContestantID);
    }, '');
};

LoadData();

/**
 * Map the columns to the grid
 * @param columns
 */
const handleCustomColumns = (columns: GridColumnOptions<ShootingResult>[]) => {
    // We add a column for the shooting result
    new GridColumnOptionsBuilder<ShootingResult>(propertyof<ShootingResult>('Misses'), 'shootingresult.misses', 'misses')
        .ReadOnly()
        .CellRenderer(DisplayMisses)
        .Insert(columns, 4);

    // We find the column for the stationId and change the renderer
    const stationColumn = columns.find((x) => x.dataField == propertyof<ShootingResult>('StationId'));
    if (stationColumn != null) {
        stationColumn.cellRenderer = ContestantShootingDisplayPoint;
    }

    // We add a button to the end of the columns to edit the time
    new GridColumnOptionsBuilder<ShootingResult>(propertyof<ShootingResult>('Id'), 'shootingresult.action', 'action')
        .ReadOnly()
        .CellRenderer(ContestantShootingToggleStateButton, {
            onClick: toggleShootingState,
            text: 'button.activate',
            text2: 'button.deactivate',
        })
        .InsertAtEnd(columns);

    // Disable sorting on all columns
    columns.forEach((col) => (col.sortable = false));
    return columns;
};

/**
 * Toggle the shooting state (active/disabled)
 * @param shooting
 * @param active
 */
const toggleShootingState = (shooting: ShootingResult, active: boolean) => {
    if (active) {
        EnableTransactions([shooting.TUID!]);
    } else {
        disableTransactions([shooting.TUID!]);
    }
};

/**
 * Add a new time for the selected contestant
 */
const addShooting = () => {
    if (selectedRaceContestantId.value == null) {
        throw new Error('No valid racecontestant selected');
    }

    let raceContestant = contestant.value.RaceContestants.find((rc) => rc.ID == selectedRaceContestantId.value);

    if (!raceContestant) {
        throw new Error(`Could not find racecontestant with id ${selectedRaceContestantId.value}`);
    }

    if (contestant.value.Bib == null) {
        useToastStore().addError('contestant.addshootingrequiresbib');
        return;
    }

    useDialog().SaveCancel({
        title: 'contestant.addnewshooting',
        body: MatrixStationRoleCellShootingEditor,
        saveCallback: async (params) => {
            AddLoader(async () => {
                if (!params || !params.dialogBodyComponentInstanceRef) {
                    return;
                }
                await params.dialogBodyComponentInstanceRef.save();

                // TODO: This should be waiting for shootingresults for the contestant we just added the shooting to, not 500 random ms
                await ThreadSleep(500); // We give the backend some time to process the transactions since it is async
                LoadData();
            }, ''); // We do not want to show a loader here since the modal is still open
            return true;
        },
        componentProps: {
            raceContestantId: raceContestant.ID,
            contestantId: contestant.value.ContestantID,
            bib: contestant.value.Bib,
            raceContestantStartTime: raceContestant.StartingTime ?? null,
            editingTuid: null,
        },
        savestate: LoadState.value,
        options: {
            Size: 'md',
            DisableAnimation: true,
        },
    });
};

/**
 * Disable the selected tuids
 * @param tuids
 */
const disableTransactions = (tuids: string[]) => {
    AddLoader(async () => {
        await useTransactions().DisableTransactions(tuids);
        await ThreadSleep(500); // We give the backend some time to process the transactions since it is async
        LoadData();
    }, '');
};

/**
 * Enables the selected transactions
 * @param tuids
 */
const EnableTransactions = (tuids: string[]) => {
    AddLoader(async () => {
        await useTransactions().EnableTransactions(tuids);
        await ThreadSleep(500); // We give the backend some time to process the transactions since it is async
        LoadData();
    }, '');
};

// The toolbar actions
const toolbarActions = [
    DropdownItemButton.create({
        buttonType: 'Primary',
        translationkey: 'contestanttimepenalty.addnew',
        title: 'contestanttimepenalty.addnewtitle',
        icon: 'fa-plus',
        action: addShooting,
    }),
];

const toolbarRightActions = [
    DropdownItemButton.create({
        buttonType: 'Secondary',
        translationkey: 'general.refresh',
        icon: 'fa-sync',
        action: () => LoadData(),
    }),
];

/**
 * The row class rules for the grid
 * We want to show the current active times as Green and the times that are not active as red. Disabled times we show as grey
 */
const rowClassRules: IGridRowCssRule<ShootingResult>[] = [
    {
        className: 'grid-row-success',
        func: (data: ShootingResult) => !data.Disabled,
    },
    // {
    //     className: 'grid-row-warning',
    //     func: (data: ShootingResult) => data.RaceContestantId != selectedRaceContestantId.value,
    // },

    {
        className: 'grid-row-disabled',
        func: (data: ShootingResult) => data.Disabled,
    },
];

/**
 * Callback when the visibility of the component changes
 * @param isVisible
 */
const onIsVisible = (isVisible: boolean) => {
    // We add a temporary keyboard shortcut for CTRL+INSERT to add a new time
    if (isVisible) {
        useKeyboardListener().addKeyboardShortcut({ alias: 'contestanttimepenaltyaddnew', on: 'CTRL+INSERT', action: addShooting });
    } else {
        // We make sure we remove the keyboard shortcut when the component is not visible
        useKeyboardListener().removeKeyboardShortcut('contestanttimepenaltyaddnew');
    }
};

/**
 * Make sure we set the selectedRaceContestant when the selectedRaceContestantId changes
 */
watch(contestant.value.RaceContestants, (nv, ov) => {
    if (selectedRaceContestantId.value == null && nv?.length > 0) {
        selectedRaceContestantId.value = nv[0].ID;
    }
});

// Just trigger a data updated when we change the race contestants. We do this so we also run the row rules
watch(selectedRaceContestantId, () => listComp.value?.dataUpdated());
</script>
<template>
    <ContentComp
        v-if="contestant && selectedRaceContestantId != null"
        class="px-0"
        :height="50"
        :load-state="LoadState"
        :show-as-modal="true"
        @is-visible="onIsVisible"
    >
        <ToolbarComp>
            <template #first>
                <DropdownSelect
                    v-if="computedRacecontestantsAsSelectline.length > 1"
                    :data="computedRacecontestantsAsSelectline"
                    v-model="selectedRaceContestantId"
                />
                <ButtonGroup :actions="toolbarActions" />
            </template>
            <template #last>
                <ButtonGroup :actions="toolbarRightActions" />
            </template>
        </ToolbarComp>
        <ListComp
            ref="listComp"
            class="overflow-auto h-100"
            v-model="data"
            :gui="useGuiStore().ShootingResult"
            :select-all="false"
            :hide-toolbar="true"
            :readonly="true"
            :row-class-rules="rowClassRules"
            :custom-columns="handleCustomColumns"
            resize-strategy="sizeColumnsToFit"
            :hide-statusbar="true"
        />
    </ContentComp>
</template>
